import { useEffect, useState } from "react";
import { useSessionUser } from "@/store/authStore";
import { markNewBadgeSeen, readNewBadgeSeen } from "@/lib/newBadge";

/**
 * A hint shown once per person (the dark-paper hint, REPLY-2026-10-06d §2).
 *
 * Like the New badge, "seen" is a per-viewer convenience in localStorage, keyed
 * per user so a shared machine shows each person their own. Storage can be
 * missing or throw; then the hint may show again, which is harmless.
 */
export const ONCE_HINTS = {
  /** "Shown darker in Dark mode. The PDF uses the real paper." */
  darkPaper: "dark-paper",
} as const;

export function onceHintKey(hintId: string, userId: string | null | undefined): string {
  return `lilia.hint.${hintId}.seen.${userId || "anon"}`;
}

/**
 * Whether the hint shows now. The first time `active` holds for this person it
 * shows, and is marked seen at once, so it never shows on a later visit; it
 * stays until dismissed or the surface goes away.
 */
export function useOnceHint(hintId: string, active: boolean): { visible: boolean; dismiss: () => void } {
  const userId = useSessionUser((s) => s.user.userId);
  const key = onceHintKey(hintId, userId);
  const [shown, setShown] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (!active || shown || readNewBadgeSeen(key)) return;
    markNewBadgeSeen(key);
    setShown(true);
  }, [active, key, shown]);
  return { visible: shown && active && !dismissed, dismiss: () => setDismissed(true) };
}
